import React, { useEffect, useState, useCallback, useRef } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightArrowLeft, faRotate,
  faArrowUp, faArrowDown, faBuilding, faCheckCircle,
  faCoins, faExclamationTriangle, faWallet,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiButton,
} from '../../components/ui';
import type { StatCardDef } from '../../components/ui';
import MerchantMultiSelect from '../../components/shared/MerchantMultiSelect';
import { fmtMoney } from '../../utils/formatMoney';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Asia/Bangkok';

const FILTER_SELECT_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';

const WalletTransactionsPage: React.FC = () => {
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [merchants, setMerchants] = useState<any[]>([]);
  const [filters, setFilters] = useState({ merchant_id: '', transaction_type: '' });

  const [dateFrom, setDateFrom] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedDateFrom, setAppliedDateFrom] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedDateTo, setAppliedDateTo] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalItems, setTotalItems] = useState(0);

  const [summary, setSummary] = useState({
    credit_count: 0, credit_amount: 0,
    debit_count: 0, debit_amount: 0,
    total_fee: 0,
  });
  const [summaryLoading, setSummaryLoading] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  const loadMerchants = async () => {
    try {
      const res = await merchantApi.get('/merchants');
      setMerchants(res.data.data || []);
    } catch { /* silent */ }
  };

  const loadTransactions = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);
      const offset = (currentPage - 1) * pageSize;
      let url = `/admin/wallet-transactions?limit=${pageSize}&offset=${offset}`;
      if (filters.merchant_id) url += `&merchant_id=${filters.merchant_id}`;
      if (filters.transaction_type) url += `&transaction_type=${filters.transaction_type}`;
      if (appliedDateFrom) url += `&date_from=${appliedDateFrom}`;
      if (appliedDateTo) url += `&date_to=${appliedDateTo}`;

      const res = await merchantApi.get(url, { signal: controller.signal });
      if (!controller.signal.aborted) {
        setTransactions(res.data.data || []);
        setTotalItems(res.data.pagination?.total || 0);
      }
    } catch (error: any) {
      if (error?.name !== 'AbortError' && error?.code !== 'ERR_CANCELED') {
        toast.error('ไม่สามารถโหลดข้อมูลได้');
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [filters.merchant_id, filters.transaction_type, currentPage, pageSize, appliedDateFrom, appliedDateTo]);

  const loadSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      let url = `/admin/wallet-transactions/summary?`;
      if (filters.merchant_id) url += `&merchant_id=${filters.merchant_id}`;
      if (filters.transaction_type) url += `&transaction_type=${filters.transaction_type}`;
      if (appliedDateFrom) url += `&date_from=${appliedDateFrom}`;
      if (appliedDateTo) url += `&date_to=${appliedDateTo}`;

      const res = await merchantApi.get(url);
      if (res.data.data) setSummary(res.data.data);
    } catch { /* silent */ }
    finally { setSummaryLoading(false); }
  }, [filters.merchant_id, filters.transaction_type, appliedDateFrom, appliedDateTo]);

  useEffect(() => { loadMerchants(); }, []);
  useEffect(() => { loadTransactions(); }, [loadTransactions]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const handleSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return;
    }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setCurrentPage(1);
  };

  const handleReset = () => {
    const today = dayjs().tz(TZ).format('YYYY-MM-DD');
    setDateFrom(today);
    setDateTo(today);
    setAppliedDateFrom(today);
    setAppliedDateTo(today);
    setFilters({ merchant_id: '', transaction_type: '' });
    setCurrentPage(1);
  };

  const netAmount = summary.credit_amount - summary.debit_amount;

  const statCards: StatCardDef[] = [
    {
      icon: faArrowUp,
      iconClassName: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: 'เงินเข้า (Credit)',
      value: <span className="text-green-600 dark:text-green-400">฿{fmtMoney(summary.credit_amount)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">{summary.credit_count.toLocaleString()} รายการ</span>,
    },
    {
      icon: faArrowDown,
      iconClassName: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      label: 'เงินออก (Debit)',
      value: <span className="text-red-500 dark:text-red-400">฿{fmtMoney(summary.debit_amount)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">{summary.debit_count.toLocaleString()} รายการ</span>,
    },
    {
      icon: faExclamationTriangle,
      iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: 'ค่าธรรมเนียมรวม',
      value: <span className="text-amber-600 dark:text-amber-400">-฿{fmtMoney(summary.total_fee)}</span>,
    },
    {
      icon: netAmount >= 0 ? faCheckCircle : faArrowRightArrowLeft,
      iconClassName: netAmount >= 0
        ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400'
        : 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      label: 'ยอดสุทธิ',
      value: (
        <span className={netAmount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}>
          {netAmount >= 0 ? '+' : '-'}฿{fmtMoney(Math.abs(netAmount))}
        </span>
      ),
    },
  ];

  const columns: Column<any>[] = [
    {
      key: 'merchant',
      header: 'Merchant',
      width: '200px',
      render: (tx) => (
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 flex-shrink-0">
            <FontAwesomeIcon icon={faBuilding} className="text-xs" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-gray-800 dark:text-slate-100 text-sm truncate">{tx.merchants?.name || '-'}</p>
            <p className="text-xs text-gray-400 dark:text-slate-500 truncate">{tx.merchants?.email || ''}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'transaction_type',
      header: 'ประเภท',
      width: '110px',
      sortable: true,
      render: (tx) =>
        tx.transaction_type === 'credit' ? (
          <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-semibold inline-flex items-center gap-1 whitespace-nowrap">
            <FontAwesomeIcon icon={faArrowUp} className="text-[10px]" /> เงินเข้า
          </span>
        ) : (
          <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full text-xs font-semibold inline-flex items-center gap-1 whitespace-nowrap">
            <FontAwesomeIcon icon={faArrowDown} className="text-[10px]" /> เงินออก
          </span>
        ),
    },
    {
      key: 'amount',
      header: 'จำนวนเงิน',
      width: '120px',
      sortable: true,
      render: (tx) => (
        <span className={`font-bold text-sm tabular-nums whitespace-nowrap ${tx.transaction_type === 'credit' ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
          {tx.transaction_type === 'credit' ? '+' : '-'}฿{fmtMoney(tx.amount)}
        </span>
      ),
    },
    {
      key: 'fee_amount',
      header: 'ค่าธรรมเนียม',
      width: '110px',
      render: (tx) =>
        parseFloat(tx.fee_amount) > 0 ? (
          <span className="text-amber-600 dark:text-amber-400 text-sm font-semibold tabular-nums whitespace-nowrap">-฿{fmtMoney(tx.fee_amount)}</span>
        ) : (
          <span className="text-gray-300 dark:text-slate-600">-</span>
        ),
    },
    {
      key: 'balance_after',
      header: 'ยอดคงเหลือหลัง',
      width: '150px',
      render: (tx) => {
        if (tx.balance_after === null || tx.balance_after === undefined) {
          return <span className="text-gray-300 dark:text-slate-600">-</span>;
        }
        const isPayoutTx = tx.description?.includes('หักจาก Payout') || tx.description?.includes('หักเงิน Payout') || tx.description?.includes('เติมเงินเข้า Payout Wallet');
        return (
          <div>
            <span className="font-semibold text-gray-800 dark:text-slate-100 text-sm tabular-nums whitespace-nowrap">฿{fmtMoney(tx.balance_after)}</span>
            <span className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium ${
              isPayoutTx
                ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
            }`}>
              <FontAwesomeIcon icon={faWallet} className="text-[9px]" />
              {isPayoutTx ? 'Payout' : 'Main'}
            </span>
          </div>
        );
      },
    },
    {
      key: 'description',
      header: 'รายละเอียด',
      render: (tx) => (
        <div className="min-w-[180px] max-w-[280px]">
          <p className="text-sm text-gray-700 dark:text-slate-200 leading-snug break-words">{tx.description || '-'}</p>
          {tx.orders?.order_number && (
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 font-medium">
              Order: {tx.orders.order_number}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'created_at',
      header: 'วันที่',
      width: '120px',
      sortable: true,
      render: (tx) => (
        <div className="whitespace-nowrap">
          <p className="text-sm text-gray-700 dark:text-slate-200">{dayjs(tx.created_at).tz(TZ).format('DD/MM/YYYY')}</p>
          <p className="text-xs text-gray-400 dark:text-slate-500">{dayjs(tx.created_at).tz(TZ).format('HH:mm:ss')}</p>
        </div>
      ),
    },
  ];

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faCoins}
        title="Wallet Transactions"
        subtitle={`ยอดเงินเข้า-ออกกระเป๋าเงิน • ${totalItems.toLocaleString()} รายการ`}
      />

      <UiFilterPanel
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
        actions={
          <UiButton variant="secondary" icon={faRotate} onClick={() => { loadTransactions(); loadSummary(); }}>
            Refresh
          </UiButton>
        }
      >
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Merchant</label>
          <MerchantMultiSelect
            merchants={merchants}
            value={filters.merchant_id}
            variant="light"
            onChange={(id) => { setFilters((f) => ({ ...f, merchant_id: id })); setCurrentPage(1); }}
            placeholder="All Merchants"
          />
        </div>

        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">ประเภท</label>
          <select
            value={filters.transaction_type}
            onChange={(e) => { setFilters((f) => ({ ...f, transaction_type: e.target.value })); setCurrentPage(1); }}
            className={FILTER_SELECT_CLASS}
          >
            <option value="">ทุกประเภท</option>
            <option value="credit">เงินเข้า (Credit)</option>
            <option value="debit">เงินออก (Debit)</option>
          </select>
        </div>
      </UiFilterPanel>

      <UiStatsGrid cards={statCards} loading={summaryLoading} cols={4} />

      <DataTable
        data={transactions}
        columns={columns}
        loading={loading}
        emptyMessage="ไม่มีรายการธุรกรรม"
        emptyIcon={<FontAwesomeIcon icon={faCoins} className="text-5xl text-gray-200 dark:text-slate-600" />}
        serverPagination={{
          currentPage,
          totalItems,
          pageSize,
          onPageChange: (page) => setCurrentPage(page),
          onPageSizeChange: (size) => { setPageSize(size); setCurrentPage(1); },
        }}
        hoverable
        compact
      />
    </div>
  );
};

export default WalletTransactionsPage;
